import React, { createContext, useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { fetchLiveAvatarToken, startLiveAvatarSession, stopLiveAvatarSession } from "../services/liveAvatarService";
import { subscribeToTracks } from "../utils/avatarHelper";
import { closeSwal, showError, showLoader, showSuccess } from "../utils/sweetAlert";
import axiosInstance from "../services/axiosInstance";
import { CHARGE_CONSULTATION, USER_BASE_URL } from "../API";

export const AvatarContext = createContext();

export const AvatarProvider = ({ children }) => {
    const TOKEN = localStorage.getItem("userToken");
    const headers = { token: TOKEN }
    const [avatar, setAvatar] = useState(null);
    const [avatarReady, setAvatarReady] = useState(false);
    const [isVideoLoading, setIsVideoLoading] = useState(false);
    const [heygenError, setHeygenError] = useState(null);
    const [hasVideo, setHasVideo] = useState(false);
    const [activeConsultationId, setActiveConsultationId] = useState(null);
    const roomRef = useRef(null);
    const audioElsRef = useRef([]);
    const videoLayerRef = useRef(null);
    const paymentTriggeredRef = useRef(false);
    const activeConsultationIdRef = useRef(null);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 991);

    useEffect(() => { activeConsultationIdRef.current = activeConsultationId; }, [activeConsultationId]);
    useEffect(() => {
        if (!avatar) return;
        const room = avatar.room || avatar;

        return subscribeToTracks(room, {
            onAudioTrack: (track) => {
                const isAlreadyAttached = audioElsRef.current.some(el => el.track === track);
                if (isAlreadyAttached) return;

                const audioEl = track.attach();
                audioEl.style.display = "none";
                audioEl.track = track;
                document.body.appendChild(audioEl);
                audioElsRef.current.push(audioEl);
            },

            onTrackUnsubscribed: (track) => {
                if (track.kind === "audio") {
                    const index = audioElsRef.current.findIndex(el => el.track === track);
                    if (index !== -1) {
                        const el = audioElsRef.current[index];
                        el.remove();
                        audioElsRef.current.splice(index, 1);
                    }
                }
                if (track.kind === "video") {
                    if (!paymentTriggeredRef.current && activeConsultationIdRef.current) {
                        setTimeout(() => { processPayment(activeConsultationIdRef.current); }, 1200);
                    }
                }
            }
        });
    }, [avatar]);

    const init = async (headers) => {
        try {
            if (roomRef.current) {
                await roomRef.current.disconnect?.();
                roomRef.current = null;
            }
            setIsVideoLoading(true);
            setHeygenError(null);
            paymentTriggeredRef.current = false;

            const tokenData = await fetchLiveAvatarToken(headers);
            if (!tokenData?.token) { throw new Error("Failed to get session token") }

            const session = await startLiveAvatarSession(tokenData.token);
            if (!session) return;

            roomRef.current = session.room || session;
            setAvatar(session);
            setAvatarReady(true);

        } catch (error) {
            console.error("Avatar init error:", error);
            setHeygenError(error.message || "Failed to initialize avatar session");
            toast.error(error.message || "Failed to initialize avatar session");
            setAvatarReady(false);
        }
        finally { setIsVideoLoading(false) }
    };

    const restartLiveAvatarSession = async (headers) => {
        setIsVideoLoading(true);
        paymentTriggeredRef.current = false;
        try {
            if (roomRef.current?.disconnect) { await roomRef.current.disconnect() }

            const tokenData = await fetchLiveAvatarToken(headers);
            if (!tokenData?.token) { throw new Error("Failed to get session token for restart") }
            const session = await startLiveAvatarSession(tokenData.token);

            if (!session) return;
            if (session.state === "disconnected") { await init(headers) }
            roomRef.current = session.room || session;
            setAvatar(session);

        }
        catch (err) {
            console.error(err);
            setAvatarReady(false)
            setHeygenError(err.message || "Failed to initialize avatar session");
        }
        finally { setIsVideoLoading(false) }
    };

    const stop = async () => {
        setIsVideoLoading(true);

        try {
            if (roomRef.current?.disconnect) { await roomRef.current.disconnect() }
            if (avatar) { await stopLiveAvatarSession(avatar) }
            audioElsRef.current.forEach(el => el.remove());
            audioElsRef.current = [];
        }
        catch (error) {
            console.error("Error stopping session:", error);
            setHeygenError(error.message || "Failed");
        }
        finally {
            setAvatar(null);
            setAvatarReady(false);
            setIsVideoLoading(false);
            roomRef.current = null;
        }
    };

    const speak = async (text) => {
        if (!avatar || !text) return;
        try { await avatar.repeat(text) }
        catch (err) { console.error("Speak error:", err) }
    };

    const processPayment = async (consultationId) => {
        if (!consultationId || paymentTriggeredRef.current) return;
        paymentTriggeredRef.current = true;
        showLoader({ title: "Processing Payment", message: "Deducting amount securely from your saved card...", });

        try {
            const res = await axiosInstance.post(CHARGE_CONSULTATION, { consultationId }, { headers });
            closeSwal();
            if (res.data?.status) {
                await showSuccess({ title: "Payment Successful", message: "Consultation fee is charged successfully from your saved card", });
            }
        }
        catch (err) {
            closeSwal();
            showError({ title: "Payment Failed", message: err.response?.data?.message || "Failed to process payment. Please try again." });
        }
        finally { paymentTriggeredRef.current = false; }
    };

    return (
        <AvatarContext.Provider value={{
            avatar,
            avatarReady,
            isVideoLoading,
            heygenError,
            init, restartLiveAvatarSession, stop, speak, processPayment,
            audioElsRef,
            videoLayerRef,
            isSmallScreen, setIsSmallScreen,
            hasVideo, setHasVideo,
            activeConsultationId, setActiveConsultationId
        }}>
            {children}
        </AvatarContext.Provider>
    );
};
